import { action } from '@ember/object';
import Component from '@glimmer/component';
import { PrismInput } from '@mylighthouse/prism-ember';
import { localCopy } from 'tracked-toolbox';

interface GeneralInputsInputWithKeyboardHandlingSignature {
  Args: {
    autofocus?: boolean;
    isSmall?: boolean;
    maxLength?: number;
    onDiscard?: () => void;
    onEdit: (value: string) => void;
    placeholder?: string;
    value: string;
  };
  Element: HTMLElement;
}

export default class GeneralInputsInputWithKeyboardHandling extends Component<GeneralInputsInputWithKeyboardHandlingSignature> {
  @localCopy('args.value') declare value: string;

  discardChanges(): void {
    this.value = this.args.value;
    this.args.onDiscard?.();
  }

  setChanges(): void {
    const sanitizedValue = this.value.trim().slice(0, this.args.maxLength);
    this.args.onEdit(sanitizedValue);

    // Reset the value in case localCopy doesn't trigger due to the same sanitized value being set
    // e.g. if this.args.value = 'Value' and user enters 'Value   ' -> we call this.args.onEdit('Value') due to sanitization -> this.args.value never changed
    this.value = this.args.value;
  }

  @action onBlur(): void {
    if (this.value !== this.args.value) {
      this.setChanges();
    } else {
      this.args.onDiscard?.();
    }
  }

  @action onInput(event: Event): void {
    this.value = (event.target as HTMLInputElement).value;
  }

  @action onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.setChanges();
    } else if (event.key === 'Escape') {
      this.discardChanges();
    }
  }

  <template>
    <PrismInput
      @autofocus={{@autofocus}}
      @isSmall={{@isSmall}}
      @maxLength={{@maxLength}}
      @onBlur={{this.onBlur}}
      @onInput={{this.onInput}}
      @onKeyDown={{this.onKeyDown}}
      @placeholder={{@placeholder}}
      @removePadding={{true}}
      @type="text"
      @value={{this.value}}
      data-test-id="input-with-keyboard-handling"
      ...attributes
    />
  </template>
}
